import type { PostTheme, WebTheme } from '../types';

const LINE_HEIGHT_OPTIONS = {
  Less: 1.2,
  Normal: 1.5,
  More: 1.8,
};

// When adding new fonts to the Tiptap font registry, add a corresponding entry here
// so the font renders with graceful fallback stacks before/if the web font fails to load.
const FONT_FALLBACKS: Record<string, string> = {
  'Alegreya Sans': 'Georgia,serif',
  Alegreya: 'Merriweather,Georgia,serif',
  Arial: 'Helvetica,sans-serif',
  Bitter: "'Lucida Bright','PT Serif',Georgia,serif",
  Cormorant: "Playfair Display,'Times New Roman',serif",
  Courier: "'Lucida Typewriter',monospace",
  'DM Sans': 'Lato,Montserrat,sans-serif',
  Didot: "'Didot LT STD',Garamond,'Times New Roman',serif",
  'EB Garamond': "Garamond,'Times New Roman',serif",
  Eczar: "Lora,'DejaVu Serif',Georgia,serif",
  'Fragment Mono': "Courier,'Lucida Console',Monaco,monospace",
  Garamond: "Baskerville,'Times New Roman',serif",
  Georgia: "'Times New Roman',serif",
  'Gothic A1': 'Arial,Helvetica,sans-serif',
  'Helvetica Neue Condensed Black': 'Helvetica,Arial,sans-serif',
  'Helvetica Now': 'Inter,Roboto,Helvetica,sans-serif',
  Helvetica: 'Arial,sans-serif',
  'IBM Plex Sans': "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
  'Instrument Sans': 'Helvetica,Arial,sans-serif',
  'Instrument Serif': "Georgia,'Times New Roman',serif",
  Inter: '-apple-system,BlinkMacSystemFont,Roboto,sans-serif',
  Karla: '-apple-system,BlinkMacSystemFont,Roboto,sans-serif',
  Lato: "'Open Sans','Segoe UI',Tahoma,sans-serif",
  'Libre Baskerville': "'DejaVu Serif',Garamond,Georgia,serif",
  'Libre Franklin': "'Source Sans Pro','Trebuchet MS',Helvetica,sans-serif",
  Lora: "Palatino,'Book Antiqua',Georgia,serif",
  'Lucida Console': 'Monaco,monospace',
  Merriweather: "'DejaVu Serif',Georgia,serif",
  Monaco: "'Lucida Console',monospace",
  Montserrat: "'DejaVu Sans',Verdana,sans-serif",
  'Noto Sans': "Roboto,'Open Sans',Arial,sans-serif",
  'Noto Serif': "Didot,'Times New Roman',serif",
  'Open Sans': "'Segoe UI','Apple SD Gothic Neo','Lucida Grande','Lucida Sans Unicode',sans-serif",
  Oswald: "Montserrat,'Lucida Sans Unicode',sans-serif",
  'PT Sans': "Helvetica,'Arial Unicode MS',sans-serif",
  'PT Serif': "Georgia,'Times New Roman',serif",
  Palatino: "'Palatino Linotype','Palatino LT STD',Georgia,serif",
  'Playfair Display': "Didot,'Bodoni MT','Times New Roman',serif",
  Poppins: 'Helvetica,sans-serif',
  Raleway: "'Open Sans',Montserrat,sans-serif",
  Roboto: '-apple-system,BlinkMacSystemFont,Tahoma,sans-serif',
  'Source Sans 3': "'Source Sans Pro',Roboto,sans-serif",
  'Space Grotesk': 'Helvetica,Arial,sans-serif',
  'Space Mono': "Courier,'Lucida Console',Monaco,monospace",
  Spectral: 'Georgia,Palatino,serif',
  Syne: 'Helvetica,Arial,sans-serif',
  Taviraj: 'Palatino,Didot,serif',
  'Times New Roman': 'Baskerville,Georgia,serif',
  'Trebuchet MS': "'Lucida Grande',Tahoma,sans-serif",
  Verdana: 'Geneva,sans-serif',
  'Work Sans': "'Lucida Grande',Verdana,sans-serif",
};

const INLINE_LINK_THEME_STYLES = {
  inline_link_italic: { variable: '--wt-inline-link-font-style', value: 'italic' },
  inline_link_bold: { variable: '--wt-inline-link-font-weight', value: 'bold' },
  inline_link_underline: { variable: '--wt-inline-link-underline', value: 'underline' },
} as const;
type InlineLinkThemeStyleKey = keyof typeof INLINE_LINK_THEME_STYLES;

const RSS_CTA_LINK_THEME_STYLES = {
  rss_cta_link_italic: { variable: '--wt-rss-cta-link-font-style', value: 'italic' },
  rss_cta_link_bold: { variable: '--wt-rss-cta-link-font-weight', value: 'bold' },
  rss_cta_link_underline: { variable: '--wt-rss-cta-link-underline', value: 'underline' },
} as const;
type RssCtaLinkThemeStyleKey = keyof typeof RSS_CTA_LINK_THEME_STYLES;

const isThemeToggleEnabled = (value: unknown): boolean => value === true || value === 'true';
const isInlineLinkThemeStyleKey = (key: string): key is InlineLinkThemeStyleKey => {
  return Object.hasOwn(INLINE_LINK_THEME_STYLES, key);
};
const isRssCtaLinkThemeStyleKey = (key: string): key is RssCtaLinkThemeStyleKey => {
  return Object.hasOwn(RSS_CTA_LINK_THEME_STYLES, key);
};

const HEADING_COLOR_KEY_PATTERN = /^h[1-6]_font_color$/;
const DEFAULT_HEADING_COLORS = new Set(['#111827', '#2a2a2a']);

const isDefaultHeadingColor = (key: string, value: unknown): boolean => {
  return (
    HEADING_COLOR_KEY_PATTERN.test(key) && typeof value === 'string' && DEFAULT_HEADING_COLORS.has(value.toLowerCase())
  );
};

export const formatPostThemeFontFamily = (fontFamily: string): string => {
  const normalizedFontFamily = fontFamily.trim();
  if (!normalizedFontFamily) {
    return fontFamily;
  }

  if (normalizedFontFamily.includes(',')) {
    return normalizedFontFamily;
  }

  const fallback = FONT_FALLBACKS[normalizedFontFamily];
  return fallback ? `'${normalizedFontFamily}',${fallback}` : `'${normalizedFontFamily}'`;
};
const getLineHeightCssValue = (lineHeight: string): string => {
  const value = LINE_HEIGHT_OPTIONS[lineHeight as keyof typeof LINE_HEIGHT_OPTIONS] ?? LINE_HEIGHT_OPTIONS.Normal;

  return value.toString();
};

const getGenericEmbedLineHeightCssValue = (fontSize: string, fallbackPx: number): string => {
  const parsedFontSize = Number.parseFloat(fontSize);
  const effectiveFontSize = Number.isNaN(parsedFontSize) ? fallbackPx : parsedFontSize;

  return `${Math.ceil(effectiveFontSize * 1.4)}px`;
};

export const overridePostThemeFontFamily = (theme: PostTheme, fontFamily?: string): PostTheme => {
  if (!fontFamily) {
    return theme;
  }

  return Object.fromEntries(
    Object.entries(theme).map(([key, value]) => (key.endsWith('_font_family') ? [key, fontFamily] : [key, value])),
  ) as PostTheme;
};

export const postThemeToCSSVariables = (
  theme: PostTheme,
  { isWeb = false }: { isWeb?: boolean } = {},
): Record<string, string> => {
  return Object.entries(theme).reduce(
    (acc, [key, value]) => {
      if (key === 'header_custom_css') {
        return acc;
      }
      if (isWeb && isDefaultHeadingColor(key, value)) {
        return acc;
      }

      const newKey = `--wt-${key.replaceAll('_', '-')}`;
      if (isInlineLinkThemeStyleKey(key)) {
        const inlineLinkStyle = INLINE_LINK_THEME_STYLES[key];
        if (isThemeToggleEnabled(value)) {
          acc[inlineLinkStyle.variable] = inlineLinkStyle.value;
        } else {
          if (key === 'inline_link_bold') {
            acc[inlineLinkStyle.variable] = 'inherit';
          } else if (key === 'inline_link_italic') {
            acc[inlineLinkStyle.variable] = 'inherit';
          } else if (key === 'inline_link_underline') {
            acc[inlineLinkStyle.variable] = 'none';
          }
        }
        return acc;
      }
      if (isRssCtaLinkThemeStyleKey(key)) {
        const rssCtaLinkStyle = RSS_CTA_LINK_THEME_STYLES[key];
        if (isThemeToggleEnabled(value)) {
          acc[rssCtaLinkStyle.variable] = rssCtaLinkStyle.value;
          if (key === 'rss_cta_link_underline') {
            acc['--wt-rss-cta-link-border-width'] = '1px';
          }
        } else {
          if (key === 'rss_cta_link_bold') {
            acc[rssCtaLinkStyle.variable] = 'normal';
          } else if (key === 'rss_cta_link_italic') {
            acc[rssCtaLinkStyle.variable] = 'normal';
          } else if (key === 'rss_cta_link_underline') {
            acc[rssCtaLinkStyle.variable] = 'none';
            acc['--wt-rss-cta-link-border-width'] = '0';
          }
        }
        return acc;
      }
      if (key.endsWith('_font_family') && typeof value === 'string') {
        const formattedFontFamily = formatPostThemeFontFamily(value);

        acc[newKey] = formattedFontFamily;

        if (key === 'hyperlink_font_family') {
          acc['--wt-hyperlink-font'] = formattedFontFamily;
        }

        return acc;
      }
      if (key === 'hyperlink_title_size' && typeof value === 'string') {
        acc[newKey] = value;
        acc['--wt-generic-embed-title-line-height'] = getGenericEmbedLineHeightCssValue(value, 20);
        return acc;
      }
      if (key === 'hyperlink_description_size' && typeof value === 'string') {
        acc[newKey] = value;
        acc['--wt-generic-embed-description-line-height'] = getGenericEmbedLineHeightCssValue(value, 16);
        return acc;
      }
      if (key === 'hyperlink_link_size' && typeof value === 'string') {
        acc[newKey] = value;
        acc['--wt-generic-embed-host-line-height'] = getGenericEmbedLineHeightCssValue(value, 12);
        return acc;
      }
      if (key.includes('line_height_size') && typeof value === 'string') {
        const lineHeightCssValue = getLineHeightCssValue(value);
        acc[`${newKey}-original`] = value.toLowerCase() === 'normal' ? 'normal' : lineHeightCssValue;
        acc[newKey] = lineHeightCssValue;
        return acc;
      }

      acc[newKey] = value;

      return acc;
    },
    {} as Record<string, string>,
  );
};

const EXCLUDED_WEB_THEME_KEYS = ['content_theme', 'name', 'owner_id', 'purpose', 'subscribe_theme'];
export const webThemeToCSSVariables = (theme: WebTheme): Record<string, string> => {
  return Object.entries(theme).reduce(
    (acc, [key, value]) => {
      if (!EXCLUDED_WEB_THEME_KEYS.includes(key)) {
        const newKey = `--site-theme-${key.replaceAll('_', '-')}`;
        acc[newKey] = value;
      }

      return acc;
    },
    {} as Record<string, string>,
  );
};

// The post theme's background_color is also emitted as --wt-background-color, which shadows the web theme's
// value inside post content, so the subscriber break reads its colors from these instead.
export const subscriberBreakCSSVariables = (theme: WebTheme): Record<string, string> => ({
  '--pt-subscriber-break-background-color': theme.text_on_background_color,
  '--pt-subscriber-break-text-color': theme.background_color,
});
